import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Building2, Mail, Briefcase, MapPin, Calendar, FileText,
  FolderKanban, FlaskConical, ClipboardCheck, ExternalLink, CreditCard,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useUserTrackingDetail } from '@/hooks/useUserTracking';

interface Props {
  userId: string | null;
  onClose: () => void;
}

export function UserTrackingDetailModal({ userId, onClose }: Props) {
  const { data, isLoading, isError, error } = useUserTrackingDetail(userId);
  const navigate = useNavigate();

  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="p-6 border-b flex-shrink-0">
          <DialogTitle>Detalle de participante</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-1/2" />
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : isError ? (
            <div className="text-center py-8 space-y-2">
              <p className="text-destructive font-medium">Error cargando el detalle</p>
              <p className="text-xs text-muted-foreground">{(error as any)?.message ?? String(error)}</p>
              <p className="text-xs text-muted-foreground">Verificá que la migration <code>20260723120000_user_tracking_license_filter.sql</code> haya corrido.</p>
            </div>
          ) : !data ? (
            <p className="text-center text-muted-foreground py-8">Sin datos.</p>
          ) : !data.profile ? (
            <p className="text-center text-muted-foreground py-8">No se pudo cargar el detalle. Puede ser que RLS bloquee el acceso a este usuario.</p>
          ) : (
            <>
              {/* Perfil */}
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{data.profile.full_name ?? '—'}</CardTitle>
                      <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                        <Mail className="w-3 h-3" /> {data.profile.email}
                      </p>
                    </div>
                    {data.profile.avatar_url && (
                      <img
                        src={data.profile.avatar_url}
                        alt=""
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  {data.profile.organization_name && (
                    <InfoItem icon={<Building2 className="w-4 h-4" />} label="Organización" value={data.profile.organization_name} />
                  )}
                  {data.profile.company && data.profile.company !== data.profile.organization_name && (
                    <InfoItem icon={<Briefcase className="w-4 h-4" />} label="Empresa" value={data.profile.company} />
                  )}
                  {data.profile.position && <InfoItem icon={<Briefcase className="w-4 h-4" />} label="Cargo" value={data.profile.position} />}
                  {data.profile.area && <InfoItem icon={<MapPin className="w-4 h-4" />} label="Área" value={data.profile.area} />}
                </CardContent>
              </Card>

              {/* Licencia */}
              {data.license && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Licencia
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex items-center gap-3 text-sm flex-wrap">
                    <Badge className={
                      data.license.license_code === 'pro'        ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30' :
                      data.license.license_code === 'enterprise' ? 'bg-purple-500/20 text-purple-500 border-purple-500/30'  :
                      data.license.license_code === 'starter'    ? 'bg-blue-500/20 text-blue-500 border-blue-500/30'       :
                      'bg-slate-500/20 text-slate-500 border-slate-500/30'
                    }>
                      {data.license.license_name ?? data.license.license_code}
                    </Badge>
                    {data.license.status && (
                      <span className="text-xs text-muted-foreground">Status: <b>{data.license.status}</b></span>
                    )}
                    {data.license.provider && (
                      <span className="text-xs text-muted-foreground">Proveedor: <b>{data.license.provider}</b></span>
                    )}
                    {data.license.current_period_end && (
                      <span className="text-xs text-muted-foreground">
                        {/* Igual que en UserManagementPage: se guarda como medianoche UTC del
                            día elegido, así que se toma solo la fecha para evitar que la zona
                            local corra el día hacia atrás. */}
                        Vence: <b>{format(parseISO(`${data.license.current_period_end.slice(0, 10)}T00:00:00`), 'd MMM yyyy', { locale: es })}</b>
                      </span>
                    )}
                    {data.license.billing_cycle && (
                      <span className="text-xs text-muted-foreground">Ciclo: <b>{data.license.billing_cycle}</b></span>
                    )}
                    {data.license.price_amount != null && (
                      <span className="text-xs text-muted-foreground">
                        Precio: <b>{data.license.price_amount} {data.license.currency ?? 'USD'}</b>
                      </span>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Cohortes */}
              {data.cohorts.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Cohortes ({data.cohorts.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {data.cohorts.map(c => (
                      <div key={c.cohort_id} className="flex items-center justify-between text-sm border rounded p-2">
                        <div>
                          <p className="font-medium">{c.cohort_name}</p>
                          {c.program_name && <p className="text-xs text-muted-foreground">{c.program_name}</p>}
                        </div>
                        <div className="text-xs text-muted-foreground text-right">
                          {c.start_date && <div>{format(parseISO(c.start_date), 'd MMM yyyy', { locale: es })}</div>}
                          {c.end_date && (
                            <div>hasta {format(parseISO(c.end_date), 'd MMM yyyy', { locale: es })}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Tabs con detalle */}
              <Tabs defaultValue="deliverables">
                <TabsList className="grid grid-cols-4 w-full">
                  <TabsTrigger value="deliverables"><FileText className="w-4 h-4 mr-1" /> Entregables ({data.deliverables.length})</TabsTrigger>
                  <TabsTrigger value="projects"><FolderKanban className="w-4 h-4 mr-1" /> Proyectos ({data.projects.length})</TabsTrigger>
                  <TabsTrigger value="lab"><FlaskConical className="w-4 h-4 mr-1" /> Lab ({data.challenge_solutions.length})</TabsTrigger>
                  <TabsTrigger value="diagnostics"><ClipboardCheck className="w-4 h-4 mr-1" /> Diagnóstico ({data.diagnostics.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="deliverables" className="space-y-2">
                  {data.deliverables.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">Sin entregables.</p>
                  ) : data.deliverables.map(d => (
                    <div key={d.id} className="border rounded p-3 text-sm flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{d.name}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-[10px]">{d.status}</Badge>
                          {d.submitted_at && <span>{format(parseISO(d.submitted_at), 'd MMM yyyy', { locale: es })}</span>}
                        </div>
                      </div>
                      {d.file_url && (
                        <Button variant="ghost" size="sm" asChild>
                          <a href={d.file_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="projects" className="space-y-2">
                  {data.projects.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">Sin proyectos.</p>
                  ) : data.projects.map(p => (
                    <div key={p.id} className="border rounded p-3 text-sm flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{p.name}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          {p.status && <Badge variant="outline" className="text-[10px]">{p.status}</Badge>}
                          {p.submitted && <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30 text-[10px]">Enviado</Badge>}
                          {p.is_approved && <Badge className="bg-emerald-600/20 text-emerald-600 border-emerald-600/30 text-[10px]">Aprobado</Badge>}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/ai-portfolio/${p.id}`)}>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="lab" className="space-y-2">
                  {data.challenge_solutions.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">Sin soluciones en el laboratorio.</p>
                  ) : (
                    <div className="text-sm text-muted-foreground p-3">
                      <p>{data.challenge_solutions.length} solución(es) creada(s) en el laboratorio de retos.</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="diagnostics" className="space-y-2">
                  {data.diagnostics.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">Sin diagnósticos.</p>
                  ) : data.diagnostics.map(d => (
                    <div key={d.id} className="border rounded p-3 text-sm flex items-center justify-between">
                      <div>
                        <p className="font-medium">Nivel: {d.maturity_level ?? '—'}</p>
                        <p className="text-xs text-muted-foreground">
                          {d.completed_at
                            ? `Completado ${format(parseISO(d.completed_at), 'd MMM yyyy', { locale: es })}`
                            : 'En progreso'}
                        </p>
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InfoItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <div className="text-muted-foreground mt-0.5">{icon}</div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p>{value}</p>
      </div>
    </div>
  );
}
